import * as React from "react";
import { FunctionComponent, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { v4 } from "uuid";
import { CommonMancalaGame } from "mancala.js";
import {
    BoardViewModel, completeLesson, explainMove, fulfilsTask, Game, getMoveOutcome, getNextLessonRule, getRule,
    hideTutorialBanner, LESSON_RULES, MoveOutcome, PitAnimator, Theme,
} from "@mancala/core";
import AppHeader from "../components/headerbar/AppHeader";
import PageContainer from "../components/PageContainer";
import BoardView from "../components/board/BoardView";
import { Context } from "../context/context";
import PracticeGameCard from "../components/PracticeGameCard";

const YOU = "you";
const OPPONENT = "opponent";

// A game showing the lesson's board, with the real rules, the player at the bottom to move.
function createLessonGame(board: number[]): Game {
    const mancalaGame = new CommonMancalaGame("lesson", YOU, OPPONENT);
    board.forEach((stoneCount, index) => (mancalaGame.board.pits[index].stoneCount = stoneCount));
    mancalaGame.turnPlayerId = YOU;
    mancalaGame.state = "playing";
    return {
        id: "lesson",
        mancalaGame,
        gameUsersConnectionInfo: {
            user1ConnectionInfo: { userId: YOU, isOnline: true },
            user2ConnectionInfo: { userId: OPPONENT, isOnline: true },
        },
    };
}

type Phase = "playing" | "animating" | "done";

// One rule taught on a prepared board (docs/specs/tutorial, R2). Everything runs in the browser on the TypeScript
// engine; nothing is sent to the server.
const LessonPage: FunctionComponent<{ context: Context, theme: Theme }> = ({ context }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { ruleId } = useParams();
    const rule = getRule(ruleId);
    const lesson = rule?.lesson;
    const [game, setGame] = useState<Game | undefined>(undefined);
    const [boardViewModel, setBoardViewModel] = useState<BoardViewModel | undefined>(undefined);
    const [phase, setPhase] = useState<Phase>("playing");
    const [outcome, setOutcome] = useState<MoveOutcome | undefined>(undefined);
    const [playedIndex, setPlayedIndex] = useState<number | undefined>(undefined);
    const animator = useRef<PitAnimator | undefined>(undefined);
    const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

    const start = () => {
        if (!lesson || !animator.current) return;
        if (timer.current) clearTimeout(timer.current);
        const lessonGame = createLessonGame(lesson.board);
        setGame(lessonGame);
        setOutcome(undefined);
        setPhase("playing");
        animator.current.setNewGame(lessonGame);
    };

    useEffect(() => {
        hideTutorialBanner(context.storage);
        // The animator sends the same model, changed, at every step; a new one makes React draw each step.
        animator.current = new PitAnimator(context.themeManager, (model) => {
            setBoardViewModel(new BoardViewModel(v4(), model.pits));
        });
        start();
        return () => {
            if (timer.current) clearTimeout(timer.current);
            animator.current?.dispose();
        };
    }, [ruleId]);

    if (!rule || !lesson) {
        return (
            <PageContainer>
                <AppHeader context={context} />
                <main className="wrap"><Link to="/learn">{t("AllLessons")}</Link></main>
            </PageContainer>
        );
    }

    const onPitSelect = (index: number) => {
        if (phase !== "playing" || !game || !(lesson.board[index] > 0) || index > 5) return;
        const moveOutcome = getMoveOutcome(lesson.board, index);
        const played = createLessonGame(lesson.board);
        played.mancalaGame.moveByPlayerPit(YOU, index);
        setGame(played);
        setPlayedIndex(index);
        setPhase("animating");
        animator.current?.setUpdatedGame(played);
        const steps = played.mancalaGame.history[0].gameSteps.length;
        timer.current = setTimeout(() => {
            setOutcome(moveOutcome);
            setPhase("done");
            if (fulfilsTask(lesson.task, moveOutcome)) completeLesson(context.storage, rule.id);
        }, (steps + 1) * animator.current!.animationInterval);
    };

    const fulfilled = outcome !== undefined && fulfilsTask(lesson.task, outcome);
    const next = getNextLessonRule(rule.id);
    const number = LESSON_RULES.findIndex((r) => r.id === rule.id) + 1;

    return (
        <PageContainer>
            <AppHeader context={context} />
            <main className="wrap lesson">
                <div className="lhead">
                    <Link to="/learn" className="crumb">{t("LessonNumber", { number, count: LESSON_RULES.length })}</Link>
                    <h1>{t(rule.titleKey)}</h1>
                    {rule.textKeys.map((key) => <p key={key}>{t(key)}</p>)}
                </div>
                <div className={"task panel" + (fulfilled ? " ok" : "")} aria-live="polite">
                    {phase !== "done" && <strong>{t(lesson.taskKey)}</strong>}
                    {phase === "done" && outcome && (
                        <>
                            {fulfilled && <strong>{t("WellDone")}</strong>}
                            <span>{explainMove(outcome, "you", t)}</span>
                            <div className="actions">
                                {fulfilled && next && (
                                    <button className="btn pri" onClick={() => navigate(`/learn/${next.id}`)}>{t("NextLesson")}</button>
                                )}
                                {fulfilled && !next && (
                                    <button className="btn pri" onClick={() => navigate("/learn")}>{t("AllLessons")}</button>
                                )}
                                <button className={"btn " + (fulfilled ? "ghost" : "pri")} onClick={start}>{t("TryAgain")}</button>
                            </div>
                        </>
                    )}
                </div>
                {fulfilled && !next && <PracticeGameCard context={context} />}
                {game && boardViewModel && (
                    <div className="lboard">
                        <BoardView
                            game={game}
                            boardViewModel={boardViewModel}
                            context={context}
                            revert={false}
                            topName={t("Opponent")}
                            bottomName={t("You")}
                            onPitSelect={(index) => onPitSelect(index)}
                            canPlay={phase === "playing"}
                            lastMoveIndex={phase === "playing" ? undefined : outcome?.pitIndex ?? playedIndex} />
                    </div>
                )}
            </main>
            <style jsx>{`
                .lesson {
                    max-width: 980px;
                    width: 100%;
                    box-sizing: border-box;
                    align-self: center;
                }
                .lhead {
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                }
                .lesson :global(.crumb) {
                    color: var(--muted);
                    font-weight: 700;
                    font-size: 14px;
                    text-decoration: none;
                }
                h1 {
                    margin: 0;
                    font-size: 28px;
                    letter-spacing: -.02em;
                }
                p {
                    margin: 0;
                    color: var(--muted);
                    line-height: 1.5;
                }
                .task {
                    padding: 16px 20px;
                    display: flex;
                    flex-direction: column;
                    gap: 10px;
                    flex-shrink: 0;
                }
                .task.ok {
                    box-shadow: inset 0 0 0 2px var(--accent);
                }
                .actions {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 10px;
                }
                .lboard {
                    position: relative;
                    min-width: 0;
                    flex-shrink: 0;
                }
            `}</style>
        </PageContainer>
    );
};

export default LessonPage;
